import type { TokenPair } from '@bk/contracts';
import { useState, type FormEvent } from 'react';
import { api, ApiRequestError, errorMessage, session } from '../lib/api';
import { APP_NAME } from '../lib/app-name';
import { AppMark, Button, ErrorText, Field } from '../ui';

export function LoginPage({ onAuthenticated }: { onAuthenticated: () => void }) {
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [otp, setOtp] = useState('');
  const [needsOtp, setNeedsOtp] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      const pair =
        mode === 'login'
          ? await api<TokenPair>('/auth/login', { body: { email, password, ...(needsOtp ? { otp } : {}) } })
          : await api<TokenPair>('/auth/register', { body: { email, password, name } });
      session.store(pair);
      onAuthenticated();
    } catch (e) {
      if (e instanceof ApiRequestError && e.body.code === 'mfa_required') {
        setNeedsOtp(true);
      } else {
        setError(e instanceof ApiRequestError && e.body.code === 'validation_error' ? 'Controlla i campi: la password deve avere almeno 10 caratteri.' : errorMessage(e));
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="flex min-h-screen bg-paper">
      {/* Fascia di sinistra sullo schermo grande: il marchio, nello stesso colore dell'app. */}
      <section className="relative hidden w-[42%] max-w-xl flex-col justify-between overflow-hidden bg-accent p-10 text-white lg:flex">
        <div className="absolute -right-24 -top-24 h-80 w-80 rounded-full bg-white/10" aria-hidden />
        <div className="absolute -bottom-32 -left-20 h-96 w-96 rounded-full bg-white/[0.07]" aria-hidden />
        <span className="relative flex items-center gap-3">
          <span className="grid h-10 w-10 place-items-center rounded-[11px] bg-white text-accent"><AppMark className="h-full w-full" /></span>
          <span className="text-xl font-semibold tracking-tight">{APP_NAME}</span>
        </span>
        <div className="relative">
          <p className="text-3xl font-semibold leading-tight">La tua azienda, ordinata a partire dai suoi dati.</p>
          <p className="mt-3 max-w-sm text-sm text-white/75">Clienti, fornitori, beni, procedure e controlli in un solo posto, navigabili da una cosa all’altra.</p>
        </div>
        <span className="relative text-xs text-white/60">© {new Date().getFullYear()} {APP_NAME}</span>
      </section>
      <section className="flex flex-1 items-center justify-center px-4 py-10">
        <div className="w-full max-w-sm">
          <span className="mb-8 flex items-center gap-2.5 lg:hidden">
            <span className="grid h-9 w-9 place-items-center rounded-[10px] bg-accent text-white"><AppMark className="h-full w-full" /></span>
            <span className="text-lg font-semibold tracking-tight">{APP_NAME}</span>
          </span>
          <div className="rounded-xl bg-white p-7 shadow-[0_0_20px_rgba(8,21,66,0.05)]">
            <h1 className="text-xl font-semibold">{mode === 'login' ? 'Accedi' : 'Crea il tuo account'}</h1>
            <p className="mb-6 mt-1 text-sm text-ink/55">{mode === 'login' ? 'Accedi per entrare nelle tue aziende.' : 'Bastano nome, email e una password.'}</p>
            <form className="space-y-4" onSubmit={submit}>
              {mode === 'register' && <Field label="Nome" value={name} onChange={(e) => setName(e.target.value)} required autoComplete="name" />}
              <Field label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="email" />
              <Field
                label="Password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={mode === 'register' ? 10 : 1}
                autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
              />
              {needsOtp && (
                <Field label="Codice dell'app di autenticazione" inputMode="numeric" pattern="\d{6}" maxLength={6} value={otp} onChange={(e) => setOtp(e.target.value)} autoFocus required autoComplete="one-time-code" />
              )}
              <ErrorText>{error}</ErrorText>
              <Button type="submit" disabled={busy} className="w-full py-2.5">
                {busy ? 'Attendere…' : mode === 'login' ? 'Accedi' : 'Crea account'}
              </Button>
            </form>
          </div>
          <p className="mt-5 text-center text-sm text-ink/60">
            {mode === 'login' ? 'Non hai un account?' : 'Hai già un account?'}{' '}
            <button
              type="button"
              className="font-medium text-accent hover:underline"
              onClick={() => {
                setMode(mode === 'login' ? 'register' : 'login');
                setNeedsOtp(false);
                setError('');
              }}
            >
              {mode === 'login' ? 'Registrati' : 'Accedi'}
            </button>
          </p>
        </div>
      </section>
    </main>
  );
}
